import {
  COPILOT_COACHING_LABELS,
  PLAYBOOK_STAGE_LABELS,
  STEP_STATE_LABELS,
  SUGGESTION_TYPE_LABELS,
  VERIFICATION_LABELS,
  type CopilotCoachingState,
  type CopilotSessionState,
  type StepObservation,
  type StepState,
  type Suggestion,
  type SuggestionSource,
  type Utterance,
} from '@lia/contracts';
import {
  ArrowDown01Icon,
  ArrowUp01Icon,
  BrowserIcon,
  Cancel01Icon,
  CheckmarkCircle02Icon,
  LinkSquare02Icon,
  PinIcon,
  QuoteDownIcon,
  Search01Icon,
  TestTube01Icon,
} from '@hugeicons/core-free-icons';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { formatDateTime, formatTime } from '../../lib/format.js';
import { StatusBadge } from '../primitives/badge.js';
import { Button } from '../primitives/button.js';
import { Card } from '../primitives/card.js';
import { Sheet } from '../primitives/dialog.js';
import { Icon } from '../primitives/icon.js';
import { FormField } from '../primitives/form-field.js';
import { Input } from '../primitives/input.js';
import { Select } from '../primitives/select.js';
import { Kbd } from '../primitives/kbd.js';
import { InlineNotice } from '../primitives/notice.js';
import { Spinner } from '../primitives/spinner.js';
import {
  COPILOT_COACHING_TONE,
  COPILOT_CONNECTION_LABELS,
  COPILOT_CONNECTION_TONE,
  COPILOT_SESSION_STATE_LABELS,
  COPILOT_SESSION_STATE_TONE,
  SOURCE_KIND_LABELS,
  SPEAKER_LABELS,
  STEP_STATE_TONE,
  SUGGESTION_STATUS_LABELS,
  SUGGESTION_STATUS_TONE,
  SUGGESTION_TYPE_TONE,
  TRANSCRIPT_FRESHNESS_LABELS,
  TRANSCRIPT_FRESHNESS_TONE,
  VERIFICATION_TONE,
  type CopilotConnectionState,
  type TranscriptFreshness,
} from './copilot-status.js';
import { FreshnessIndicator } from './freshness-indicator.js';

// Live Call Copilot presentational components (addendum §6–§7). Data comes by props; callbacks report the advisor's
// actions. Nothing here fetches, and nothing renders sample content on its own.

// ───────────── Simulation ─────────────

export function SimulationBadge({ className }: { className?: string }) {
  return (
    <StatusBadge tone="info" icon={<Icon icon={TestTube01Icon} aria-hidden />} className={className}>
      Simulación
    </StatusBadge>
  );
}

/** Shown on every screen of a simulator session: practice data, never a real call or a provider validation. */
export function SimulationBanner({ className }: { className?: string }) {
  return (
    <InlineNotice tone="warning" role="note" icon={<Icon icon={TestTube01Icon} aria-hidden />} className={className}>
      <strong className="font-semibold">Simulación.</strong> No es una llamada real; no cuenta como validación.
    </InlineNotice>
  );
}

// ───────────── Header ─────────────

export interface CopilotSessionHeaderProps {
  contactName: string | null;
  projectName: string | null;
  playbookLabel?: string | null;
  lineName?: string | null;
  advisorName?: string | null;
  direction?: 'inbound' | 'outbound' | null;
  sessionState: CopilotSessionState;
  coaching: CopilotCoachingState;
  /** This viewer's realtime connection (not the telephone call). */
  connection: CopilotConnectionState;
  transcript: TranscriptFreshness;
  lastTranscriptAt: string | null;
  timezone: string;
  simulated: boolean;
  /** Why an ended session was closed without a provider end event (reconciliation); shown as text. */
  endReason?: string | null;
  /** Heading level of the contact name (1 on the full page, 2 inside the compact window). */
  headingLevel?: 1 | 2;
  actions?: React.ReactNode;
  compact?: boolean;
  className?: string;
}

export function CopilotSessionHeader(p: CopilotSessionHeaderProps) {
  const H = p.headingLevel === 2 ? 'h2' : 'h1';
  const meta = [
    p.projectName ?? 'Sin proyecto',
    p.playbookLabel ?? null,
    p.direction === 'inbound' ? 'Entrante' : p.direction === 'outbound' ? 'Saliente' : null,
    p.lineName ?? null,
    p.advisorName ? `Asesor: ${p.advisorName}` : null,
  ].filter(Boolean);
  return (
    <header className={cn('grid gap-2', p.className)}>
      <div className="flex flex-wrap items-start gap-2">
        <div className="grid min-w-0 flex-1 gap-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <H className={cn('font-semibold text-text-primary', p.compact ? 'text-body-lg' : 'text-title')}>{p.contactName ?? 'Contacto sin identificar'}</H>
            {p.simulated ? <SimulationBadge /> : null}
          </div>
          <p className="text-meta text-text-secondary">{meta.join(' · ')}</p>
        </div>
        {p.actions ? <div className="flex flex-wrap items-center gap-2">{p.actions}</div> : null}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <StatusBadge tone={COPILOT_SESSION_STATE_TONE[p.sessionState]}>{COPILOT_SESSION_STATE_LABELS[p.sessionState]}</StatusBadge>
        <StatusBadge tone={COPILOT_COACHING_TONE[p.coaching]}>{COPILOT_COACHING_LABELS[p.coaching]}</StatusBadge>
        <span role="status" aria-live="polite" className="contents">
          <StatusBadge tone={COPILOT_CONNECTION_TONE[p.connection]} icon={p.connection === 'connecting' || p.connection === 'resyncing' ? <Spinner size="sm" /> : undefined}>
            {COPILOT_CONNECTION_LABELS[p.connection]}
          </StatusBadge>
          <StatusBadge tone={TRANSCRIPT_FRESHNESS_TONE[p.transcript]}>{TRANSCRIPT_FRESHNESS_LABELS[p.transcript]}</StatusBadge>
        </span>
        {p.lastTranscriptAt && p.transcript !== 'hidden' ? (
          <span className="ef-tabular text-meta text-text-secondary">Último texto: {formatTime(p.lastTranscriptAt, p.timezone)}</span>
        ) : null}
      </div>
      {p.sessionState === 'ended' && p.endReason ? <p className="text-meta text-text-secondary">{p.endReason}</p> : null}
    </header>
  );
}

// ───────────── Suggestions ─────────────

export interface SuggestionCardProps {
  suggestion: Suggestion;
  variant: 'primary' | 'secondary' | 'compact';
  timezone: string;
  pinned?: boolean;
  /** Muted look and a «puede estar desactualizada» note (no updates are arriving). */
  stale?: boolean;
  onUse?: (id: string) => void;
  onDismiss?: (id: string) => void;
  onPin?: (id: string) => void;
  onShowSources?: (id: string) => void;
  /** Id of the action in flight (disables the buttons). */
  busy?: boolean;
  defaultExpanded?: boolean;
  className?: string;
}

export function SuggestionCard({ suggestion: s, variant, timezone, pinned, stale, onUse, onDismiss, onPin, onShowSources, busy, defaultExpanded = false, className }: SuggestionCardProps) {
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const detailId = React.useId();
  const active = s.status === 'active';
  const primary = variant === 'primary';
  return (
    <Card
      as="article"
      aria-label={`${primary ? 'Sugerencia principal' : 'Sugerencia'}: ${SUGGESTION_TYPE_LABELS[s.type]}`}
      className={cn('grid gap-2 p-3', primary && 'border-brand-primary p-4', stale && 'opacity-80', className)}
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <StatusBadge tone={SUGGESTION_TYPE_TONE[s.type]}>{SUGGESTION_TYPE_LABELS[s.type]}</StatusBadge>
        <StatusBadge tone={VERIFICATION_TONE[s.verification]}>{VERIFICATION_LABELS[s.verification]}</StatusBadge>
        {s.origin === 'rules' ? <StatusBadge tone="neutral">Regla del playbook</StatusBadge> : null}
        {s.simulated ? <StatusBadge tone="info" icon={<Icon icon={TestTube01Icon} aria-hidden />}>Modelo simulado</StatusBadge> : null}
        {!active ? <StatusBadge tone={SUGGESTION_STATUS_TONE[s.status]}>{SUGGESTION_STATUS_LABELS[s.status]}</StatusBadge> : null}
        {pinned ? <StatusBadge tone="accent" icon={<Icon icon={PinIcon} aria-hidden />}>Fijada</StatusBadge> : null}
        <span className="ef-tabular ml-auto text-meta text-text-secondary">{formatTime(s.createdAt, timezone)}</span>
      </div>
      <p className={cn('whitespace-pre-line text-text-primary', primary ? 'text-body-lg font-medium' : 'text-body', variant === 'compact' && 'text-body')}>{s.text}</p>
      {s.sourceRemoved ? <InlineNotice tone="warning">Una fuente citada se eliminó; el contenido ya no está disponible.</InlineNotice> : null}
      {stale ? <p className="text-meta text-warning">Puede estar desactualizada: no están llegando actualizaciones.</p> : null}
      {expanded ? (
        <div id={detailId} className="grid gap-2 rounded-control bg-surface-subtle p-2.5">
          {s.triggerExcerpt ? (
            <p className="flex gap-1.5 text-body text-text-primary">
              <Icon icon={QuoteDownIcon} aria-hidden className="mt-0.5 size-4 shrink-0 text-text-secondary" />
              <span>
                <span className="sr-only">Lo que se dijo: </span>«{s.triggerExcerpt}»
              </span>
            </p>
          ) : null}
          {s.sources.length ? <SourceList sources={s.sources} timezone={timezone} /> : <p className="text-meta text-text-secondary">Sin fuentes citadas.</p>}
          {s.latencyMs !== null ? <p className="text-meta text-text-secondary">Generada en {(s.latencyMs / 1000).toFixed(1)} s</p> : null}
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-1.5">
        {active && onUse ? (
          <Button size="sm" variant={primary ? 'primary' : 'secondary'} disabled={busy} onClick={() => onUse(s.id)} leadingIcon={<Icon icon={CheckmarkCircle02Icon} aria-hidden />}>
            Usar
          </Button>
        ) : null}
        {onPin && !pinned ? (
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => onPin(s.id)} leadingIcon={<Icon icon={PinIcon} aria-hidden />}>
            Fijar
          </Button>
        ) : null}
        {active && onDismiss ? (
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => onDismiss(s.id)} leadingIcon={<Icon icon={Cancel01Icon} aria-hidden />}>
            Descartar
          </Button>
        ) : null}
        {onShowSources && s.sources.length ? (
          <Button size="sm" variant="ghost" onClick={() => onShowSources(s.id)} leadingIcon={<Icon icon={LinkSquare02Icon} aria-hidden />}>
            Fuentes ({s.sources.length})
          </Button>
        ) : null}
        <Button
          size="sm"
          variant="ghost"
          aria-expanded={expanded}
          aria-controls={expanded ? detailId : undefined}
          onClick={() => setExpanded((v) => !v)}
          leadingIcon={<Icon icon={expanded ? ArrowUp01Icon : ArrowDown01Icon} aria-hidden />}
        >
          {expanded ? 'Ocultar detalle' : 'Ver detalle'}
        </Button>
      </div>
    </Card>
  );
}

/** Polite announcement of a new primary suggestion for screen readers (does not steal focus). */
export function NewSuggestionAnnouncer({ suggestion }: { suggestion: Suggestion | null }) {
  const [text, setText] = React.useState('');
  const last = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!suggestion || suggestion.id === last.current) return;
    last.current = suggestion.id;
    setText(`Nueva sugerencia: ${SUGGESTION_TYPE_LABELS[suggestion.type]}. ${suggestion.text}`);
  }, [suggestion]);
  return (
    <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
      {text}
    </div>
  );
}

// ───────────── Sources ─────────────

export function SourceList({ sources, timezone, className }: { sources: readonly SuggestionSource[]; timezone: string; className?: string }) {
  return (
    <ul aria-label="Fuentes" className={cn('grid gap-1.5', className)}>
      {sources.map((src) => (
        <li key={src.ref} className="grid gap-0.5 text-body">
          <span className="text-text-primary">
            <span className="font-medium">{SOURCE_KIND_LABELS[src.kind]}:</span> {src.label}
            {src.page !== null ? ` · pág. ${src.page}` : ''}
          </span>
          {src.freshness !== null || src.verifiedAt ? <FreshnessIndicator verifiedAt={src.verifiedAt} state={src.freshness ?? undefined} timezone={timezone} /> : null}
        </li>
      ))}
    </ul>
  );
}

export interface SourcesDrawerProps {
  suggestion: Suggestion | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  timezone: string;
}

export function SourcesDrawer({ suggestion, open, onOpenChange, timezone }: SourcesDrawerProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange} title="Fuentes de la sugerencia" description={suggestion ? SUGGESTION_TYPE_LABELS[suggestion.type] : undefined}>
      {suggestion ? (
        <div className="grid gap-3">
          <p className="text-body text-text-primary">{suggestion.text}</p>
          {suggestion.triggerExcerpt ? <p className="text-body text-text-secondary">Se dijo: «{suggestion.triggerExcerpt}»</p> : null}
          <StatusBadge tone={VERIFICATION_TONE[suggestion.verification]}>{VERIFICATION_LABELS[suggestion.verification]}</StatusBadge>
          {suggestion.sources.length ? <SourceList sources={suggestion.sources} timezone={timezone} /> : <p className="text-body text-text-secondary">Esta sugerencia no cita fuentes.</p>}
          <p className="text-meta text-text-secondary">Creada {formatDateTime(suggestion.createdAt, timezone)}</p>
        </div>
      ) : (
        <p className="text-body text-text-secondary">La sugerencia ya no está disponible.</p>
      )}
    </Sheet>
  );
}

// ───────────── Playbook checklist ─────────────

const CORRECTIONS: StepState[] = ['covered', 'needs_verification', 'not_applicable', 'in_progress', 'not_started'];

function ChecklistItem({ o, onCorrect, busy, readOnly, timezone }: { o: StepObservation; onCorrect?: (stepKey: string, state: StepState) => void; busy?: boolean; readOnly: boolean; timezone?: string }) {
  const [open, setOpen] = React.useState(false);
  const panelId = React.useId();
  return (
    <li className="grid gap-1.5 rounded-control border border-border-subtle bg-surface-default p-2.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="min-w-0 flex-1 text-body font-medium text-text-primary">{o.title}</span>
        <StatusBadge tone={STEP_STATE_TONE[o.state]}>{STEP_STATE_LABELS[o.state]}</StatusBadge>
      </div>
      <p className="text-meta text-text-secondary">
        {PLAYBOOK_STAGE_LABELS[o.stage]} · <span>{o.required ? 'Obligatorio' : 'Opcional'}</span>
        {o.source === 'manual' ? ` · Corregido por ${o.correctedBy ?? 'un asesor'}${o.correctedAt && timezone ? ` (${formatTime(o.correctedAt, timezone)})` : ''}` : ''}
      </p>
      {o.evidence.length ? (
        <div className="grid gap-1">
          {o.evidence.slice(0, 3).map((e) => (
            <blockquote key={e.utteranceId} className="border-l-2 border-border-control pl-2 text-meta text-text-secondary">
              <span className="sr-only">Evidencia: </span>«{e.excerpt}»
            </blockquote>
          ))}
        </div>
      ) : null}
      {o.note ? <p className="text-meta text-text-secondary">Nota: {o.note}</p> : null}
      {!readOnly && onCorrect ? (
        <div className="grid gap-1.5">
          <Button size="sm" variant="ghost" className="justify-self-start" aria-expanded={open} aria-controls={open ? panelId : undefined} aria-label={`Corregir «${o.title}»`} onClick={() => setOpen((v) => !v)}>
            Corregir
          </Button>
          {open ? (
            <div id={panelId} role="group" aria-label={`Estados para «${o.title}»`} className="flex flex-wrap gap-1.5">
              {CORRECTIONS.filter((s) => s !== o.state).map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant="secondary"
                  disabled={busy}
                  aria-label={`Marcar «${o.title}» como ${STEP_STATE_LABELS[s].toLowerCase()}`}
                  onClick={() => {
                    setOpen(false);
                    onCorrect(o.stepKey, s);
                  }}
                >
                  {STEP_STATE_LABELS[s]}
                </Button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

export interface PlaybookChecklistProps {
  observations: readonly StepObservation[];
  /** Pinned playbook version of the session; observations of earlier versions are kept, read-only. */
  currentVersionId: string | null;
  onCorrect?: (stepKey: string, state: StepState) => void;
  busyStep?: string | null;
  readOnly?: boolean;
  timezone?: string;
  className?: string;
}

/** Playbook progress. A step is covered only with transcript evidence or an advisor's correction (author shown). */
export function PlaybookChecklist({ observations, currentVersionId, onCorrect, busyStep, readOnly = false, timezone, className }: PlaybookChecklistProps) {
  const current = observations.filter((o) => o.playbookVersionId === currentVersionId);
  const earlier = observations.filter((o) => o.playbookVersionId !== currentVersionId);
  const required = current.filter((o) => o.required);
  const covered = required.filter((o) => o.state === 'covered' || o.state === 'not_applicable').length;
  return (
    <div className={cn('grid gap-2', className)}>
      {current.length ? (
        <>
          <p className="text-meta text-text-secondary">
            {covered} de {required.length} obligatorios cubiertos
          </p>
          <ol aria-label="Pasos del playbook" className="grid gap-1.5">
            {current.map((o) => (
              <ChecklistItem key={o.stepKey} o={o} onCorrect={onCorrect} busy={busyStep === o.stepKey} readOnly={readOnly} timezone={timezone} />
            ))}
          </ol>
        </>
      ) : (
        <p className="text-body text-text-secondary">{currentVersionId ? 'Aún no hay pasos observados.' : 'Sin playbook: elige uno para seguir sus pasos.'}</p>
      )}
      {earlier.length ? (
        <details className="grid gap-1.5">
          <summary className="cursor-pointer text-meta font-medium text-text-secondary">De una versión anterior del playbook</summary>
          <ol className="mt-1.5 grid gap-1.5">
            {earlier.map((o) => (
              <ChecklistItem key={`${o.playbookVersionId}:${o.stepKey}`} o={o} readOnly timezone={timezone} />
            ))}
          </ol>
        </details>
      ) : null}
    </div>
  );
}

// ───────────── Transcript ─────────────

export interface TranscriptPanelProps {
  /** null = the viewer may not read the transcript (recording rights). */
  utterances: readonly Utterance[] | null;
  timezone: string;
  className?: string;
  /** Show only the last N utterances. */
  limit?: number;
}

export function TranscriptPanel({ utterances, timezone, className, limit }: TranscriptPanelProps) {
  const ref = React.useRef<HTMLOListElement | null>(null);
  const stick = React.useRef(true);
  const list = utterances ? (limit ? utterances.slice(-limit) : utterances) : null;
  React.useEffect(() => {
    const el = ref.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [list?.length]);
  if (list === null) return <p className={cn('text-body text-text-secondary', className)}>No tienes permiso para ver la transcripción de esta llamada.</p>;
  if (!list.length) return <p className={cn('text-body text-text-secondary', className)}>Aún no hay transcripción.</p>;
  return (
    <ol
      ref={ref}
      aria-label="Transcripción"
      tabIndex={0}
      onScroll={(e) => {
        const el = e.currentTarget;
        stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
      }}
      className={cn('grid max-h-96 gap-1.5 overflow-y-auto rounded-control focus-visible:outline-2 focus-visible:outline-focus', className)}
    >
      {list.map((u) => (
        <li key={u.id} className={cn('grid gap-0.5 rounded-control p-2', u.speaker === 'internal' ? 'bg-surface-subtle' : 'bg-surface-default')}>
          <span className="flex flex-wrap items-center gap-1.5 text-meta text-text-secondary">
            <span className="font-semibold text-text-primary">{SPEAKER_LABELS[u.speaker]}</span>
            <span className="ef-tabular">{formatTime(u.sourceAt ?? u.receivedAt, timezone)}</span>
            {u.postCall ? <span>· Posterior a la llamada</span> : null}
          </span>
          <span className="text-body text-text-primary">{u.text}</span>
        </li>
      ))}
    </ol>
  );
}

// ───────────── Knowledge search ─────────────

export interface KnowledgeSearchResult {
  passages: readonly { text: string; source: SuggestionSource }[];
  structuredHint: string | null;
}

export interface KnowledgeSearchPanelProps {
  onSearch: (query: string) => void;
  result: KnowledgeSearchResult | null;
  loading?: boolean;
  error?: string | null;
  /** When set, search is disabled and this explains why. */
  disabledReason?: string | null;
  timezone?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  className?: string;
}

export function KnowledgeSearchPanel({ onSearch, result, loading, error, disabledReason, timezone = 'America/Mexico_City', inputRef, className }: KnowledgeSearchPanelProps) {
  const [q, setQ] = React.useState('');
  const id = React.useId();
  return (
    <div className={cn('grid gap-2', className)}>
      <form
        role="search"
        aria-label="Búsqueda manual de conocimiento"
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const v = q.trim();
          if (v.length >= 2) onSearch(v);
        }}
      >
        <label htmlFor={id} className="sr-only">
          Buscar en el conocimiento
        </label>
        <Input ref={inputRef} id={id} type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ej. estacionamiento, amenidades…" maxLength={300} disabled={!!disabledReason} />
        <Button type="submit" variant="secondary" loading={loading} disabled={!!disabledReason || q.trim().length < 2} leadingIcon={<Icon icon={Search01Icon} aria-hidden />}>
          Buscar
        </Button>
      </form>
      {disabledReason ? <p className="text-meta text-text-secondary">{disabledReason}</p> : null}
      {error ? (
        <InlineNotice tone="danger" role="alert">
          {error}
        </InlineNotice>
      ) : null}
      {result ? (
        <div aria-live="polite" className="grid gap-2">
          {result.structuredHint ? <InlineNotice tone="info">{result.structuredHint}</InlineNotice> : null}
          {result.passages.length ? (
            <ul aria-label="Resultados" className="grid gap-2">
              {result.passages.map((p, i) => (
                <li key={`${p.source.ref}-${i}`} className="grid gap-1 rounded-control border border-border-subtle p-2.5">
                  <p className="text-body text-text-primary">{p.text}</p>
                  <SourceList sources={[p.source]} timezone={timezone} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-body text-text-secondary">Sin resultados en el conocimiento publicado. No respondas con datos sin verificar.</p>
          )}
        </div>
      ) : null}
    </div>
  );
}

// ───────────── Compact window launcher ─────────────

export interface CompactWindowLauncherProps {
  /** Opens the companion window; returns false when the browser blocked it. */
  onOpen: () => boolean;
  className?: string;
}

/** «Abrir ventana compacta» with an explicit retry when a popup blocker stops it (addendum §7). */
export function CompactWindowLauncher({ onOpen, className }: CompactWindowLauncherProps) {
  const [blocked, setBlocked] = React.useState(false);
  const open = () => setBlocked(!onOpen());
  return (
    <div className={cn('grid gap-2', className)}>
      <Button variant="secondary" size="sm" onClick={open} leadingIcon={<Icon icon={BrowserIcon} aria-hidden />}>
        Abrir ventana compacta
      </Button>
      {blocked ? (
        <InlineNotice tone="warning" role="alert" as="div">
          <span className="grid gap-1.5">
            <span>Tu navegador bloqueó la ventana emergente. Permite ventanas emergentes para este sitio y vuelve a intentarlo.</span>
            <span>
              <Button size="sm" variant="secondary" onClick={open}>
                Reintentar
              </Button>
            </span>
          </span>
        </InlineNotice>
      ) : null}
    </div>
  );
}

// ───────────── Keyboard shortcuts ─────────────

export interface ShortcutItem {
  keys: readonly string[];
  label: string;
}

// ───────────── Project / playbook selection ─────────────

export interface ContextOption {
  id: string;
  label: string;
}

export interface ContextSelectionValue {
  projectId: string | null;
  playbookId: string | null;
  reason: string;
}

export interface ContextSelectionPanelProps {
  projects: readonly ContextOption[];
  playbooks: readonly ContextOption[];
  initialProjectId?: string | null;
  initialPlaybookId?: string | null;
  /** «select»: the mapping was ambiguous; «switch»: a deliberate mid-call change (audited, earlier evidence kept). */
  mode: 'select' | 'switch';
  onSubmit: (value: ContextSelectionValue) => void;
  onCancel?: () => void;
  busy?: boolean;
  className?: string;
}

const NONE = '__none__';

export function ContextSelectionPanel({ projects, playbooks, initialProjectId = null, initialPlaybookId = null, mode, onSubmit, onCancel, busy, className }: ContextSelectionPanelProps) {
  const [projectId, setProjectId] = React.useState<string>(initialProjectId ?? NONE);
  const [playbookId, setPlaybookId] = React.useState<string>(initialPlaybookId ?? NONE);
  const [reason, setReason] = React.useState('');
  const rid = React.useId();
  const toNull = (v: string) => (v === NONE ? null : v);
  const changed = toNull(projectId) !== initialProjectId || toNull(playbookId) !== initialPlaybookId;
  const opts = (options: readonly ContextOption[]) => [{ value: NONE, label: 'Sin elegir' }, ...options.map((o) => ({ value: o.id, label: o.label }))];
  return (
    <form
      aria-label={mode === 'select' ? 'Elegir proyecto y playbook' : 'Cambiar proyecto o playbook'}
      className={cn('grid gap-3', className)}
      onSubmit={(e) => {
        e.preventDefault();
        if (changed) onSubmit({ projectId: toNull(projectId), playbookId: toNull(playbookId), reason: reason.trim() });
      }}
    >
      <p className="text-body text-text-secondary">
        {mode === 'select'
          ? 'No pudimos determinar el proyecto o playbook de esta llamada. Hasta elegirlos solo verás orientación general, sin precios ni políticas del proyecto.'
          : 'El cambio queda registrado, borra las sugerencias del contexto anterior y conserva la evidencia ya observada con su versión original.'}
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField label="Proyecto">
          <Select value={projectId} onValueChange={setProjectId} options={opts(projects)} />
        </FormField>
        <FormField label="Playbook" description="Solo playbooks con una versión publicada.">
          <Select value={playbookId} onValueChange={setPlaybookId} options={opts(playbooks)} />
        </FormField>
      </div>
      {mode === 'switch' ? (
        <FormField label="Motivo (opcional)" id={rid}>
          <Input value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} />
        </FormField>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" size="sm" loading={busy} disabled={!changed}>
          {mode === 'select' ? 'Usar esta selección' : 'Cambiar'}
        </Button>
        {onCancel ? (
          <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
        ) : null}
      </div>
    </form>
  );
}

export function ShortcutLegend({ shortcuts, className }: { shortcuts: readonly ShortcutItem[]; className?: string }) {
  return (
    <dl aria-label="Atajos de teclado" className={cn('grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-1.5 text-meta text-text-secondary', className)}>
      {shortcuts.map((s) => (
        <React.Fragment key={s.label}>
          <dt className="flex gap-1">
            {s.keys.map((k) => (
              <Kbd key={k}>{k}</Kbd>
            ))}
          </dt>
          <dd>{s.label}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}
